// SubPromptsDetail — /admin/sub-prompts/:subPromptId. Renders the
// full prompt body + input/output schema + recent invocations
// (last 20). Cross-run pivot lives at
// /admin/products/{pid}/sub-calls/{sub_prompt_id} (separate
// ProductsSubCalls view) — we link to it per-invocation row so
// the operator can drill from a single invocation to "all of this
// sub-prompt's invocations on this product."

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import {
  EndpointUnavailableError,
  getSubPrompt,
} from "../api/traceApi";
import { BackendPendingBanner } from "../components/BackendPendingBanner";

export function SubPromptsDetail() {
  const { subPromptId = "" } = useParams();
  const { data: me } = useUser();

  const q = useQuery({
    queryKey: ["admin", "sub-prompt", subPromptId],
    queryFn: () => getSubPrompt(subPromptId),
    retry: (n, err) => !(err instanceof EndpointUnavailableError) && n < 2,
    enabled: !!subPromptId,
  });

  if (!me) return null;

  const d = q.data;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <Link
          to="/admin/sub-prompts"
          className="font-mono text-[11px] text-ink-400 hover:text-ink-100"
        >
          ← Sub-prompts
        </Link>
        <h1 className="mt-4 text-display font-medium text-ink-50 tracking-tightest mb-2">
          {subPromptId}
        </h1>
        {d?.stage && (
          <p className="font-mono text-[11px] text-ink-400 mb-6">
            stage {d.stage} · layer {d.layer ?? "—"} · failure_policy{" "}
            {d.failure_policy ?? "—"}
          </p>
        )}

        {q.error instanceof EndpointUnavailableError && (
          <BackendPendingBanner
            feature="Sub-prompt detail"
            endpoint={`GET /api/v1/admin/sub-prompts/${subPromptId}`}
          />
        )}
        {q.error && !(q.error instanceof EndpointUnavailableError) && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}
        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}

        {d && (
          <div className="space-y-6">
            <section>
              <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
                Prompt body
              </h2>
              <pre className="text-[11px] font-mono bg-ink-900 border border-ink-800 rounded p-3 overflow-auto max-h-[50vh] whitespace-pre-wrap">
                {d.prompt_body ?? "(empty)"}
              </pre>
              {d.prompt_file && (
                <p className="font-mono text-[10px] text-ink-400 mt-1">
                  file: {d.prompt_file}
                </p>
              )}
            </section>

            <div className="grid grid-cols-2 gap-6">
              <section>
                <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
                  Input schema
                </h2>
                <pre className="text-[10px] font-mono bg-ink-900 border border-ink-800 rounded p-3 overflow-auto max-h-[40vh]">
                  {d.input_schema ? JSON.stringify(d.input_schema, null, 2) : "—"}
                </pre>
              </section>
              <section>
                <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
                  Output schema
                </h2>
                <pre className="text-[10px] font-mono bg-ink-900 border border-ink-800 rounded p-3 overflow-auto max-h-[40vh]">
                  {d.output_schema ? JSON.stringify(d.output_schema, null, 2) : "—"}
                </pre>
              </section>
            </div>

            <section>
              <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
                Last 20 invocations
              </h2>
              {(d.recent_invocations ?? []).length === 0 ? (
                <p className="text-sm text-ink-400">No invocations recorded.</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {(d.recent_invocations ?? []).slice(0, 20).map((inv, i) => (
                    <li key={i} className="font-mono text-[11px] text-ink-200">
                      <span className="text-ink-400 mr-2">
                        {inv.started_at?.slice(0, 19) ?? "—"}
                      </span>
                      {inv.user_id && inv.product_id && inv.run_id ? (
                        <Link
                          to={`/users/${inv.user_id}/products/${inv.product_id}/runs/${inv.run_id}`}
                          className="text-ink-100 hover:text-accent"
                        >
                          {inv.run_id.slice(0, 12)}…
                        </Link>
                      ) : (
                        <span>{inv.run_id?.slice(0, 12) ?? "—"}</span>
                      )}
                      <span className="text-ink-400 mx-2">·</span>
                      <span>
                        {inv.item_key ?? "—"} · attempt{" "}
                        {inv.attempt ?? "?"} · {inv.outcome ?? "—"}
                        {inv.duration_ms !== undefined
                          ? ` · ${inv.duration_ms}ms`
                          : ""}
                      </span>
                      {inv.product_id && inv.user_id && (
                        <Link
                          to={`/admin/products/${inv.product_id}/sub-calls/${encodeURIComponent(subPromptId)}?uid=${encodeURIComponent(inv.user_id)}`}
                          className="ml-3 text-ink-400 hover:text-accent"
                        >
                          all on this product →
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
